/**
 * BulkImportDialog — Wiederverwendbarer CSV-Massen-Import-Dialog mit Vorschau.
 *
 * Genutzt fuer Niederlassungen und Standorte. Steuert:
 *  1. Vorlage downloaden
 *  2. CSV per Drag&Drop / Klick auswaehlen
 *  3. Dry-Run anzeigen (gruen=ok, gelb=skipped, rot=error)
 *  4. Endgueltigen Import ausloesen
 *
 * Props:
 *  - title: Dialog-Titel
 *  - downloadTemplate: () => Promise<{data: Blob}>
 *  - runImport: (file, dryRun) => Promise<{data: {summary, rows}}>
 *  - templateFilename: 'niederlassungen_vorlage.csv' (default)
 *  - onSuccess: () => void  (nach erfolgreichem Import — z.B. reload)
 */
import React, { useState } from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from './dialog';
import { Button } from './button';
import { Badge } from './badge';
import { FileDropzone } from './file-dropzone';
import { Download, CheckCircle2, AlertCircle, AlertTriangle, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';

const STATUS_INFO = {
  ok: { Icon: CheckCircle2, cls: 'text-emerald-600', label: 'OK' },
  update: { Icon: RefreshCw, cls: 'text-blue-600', label: 'Aktualisieren' },
  skipped: { Icon: AlertTriangle, cls: 'text-amber-600', label: 'Übersprungen' },
  error: { Icon: AlertCircle, cls: 'text-red-600', label: 'Fehler' },
};

export const BulkImportDialog = ({
  open, onOpenChange, title,
  downloadTemplate, runImport, templateFilename = 'vorlage.csv',
  onSuccess,
}) => {
  const [preview, setPreview] = useState(null); // {summary, rows}
  const [uploading, setUploading] = useState(false);
  const [committing, setCommitting] = useState(false);
  const [file, setFile] = useState(null);

  const reset = () => { setPreview(null); setFile(null); };

  const handleClose = () => {
    onOpenChange(false);
    setTimeout(reset, 200);
  };

  const handleDownloadTemplate = async () => {
    try {
      const res = await downloadTemplate();
      // MIME-Type und Filename aus Server-Antwort ableiten, damit XLSX/CSV
      // korrekt erkannt werden (CSV-Hartkodierung zerstoert xlsx-Bytes).
      const serverType = res.headers?.['content-type'] || res.data?.type || 'application/octet-stream';
      const disposition = res.headers?.['content-disposition'] || '';
      const match = /filename="?([^";]+)"?/i.exec(disposition);
      const filename = match?.[1] || templateFilename;
      const blob = new Blob([res.data], { type: serverType });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 5000);
    } catch (err) {
      toast.error(`Vorlage konnte nicht geladen werden: ${err.message || err}`);
    }
  };

  const handleUpload = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setFile(f);
    setUploading(true);
    try {
      const res = await runImport(f, true);
      setPreview(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message || 'Import-Vorschau fehlgeschlagen');
    } finally {
      setUploading(false);
    }
  };

  const handleCommit = async () => {
    if (!file) return;
    setCommitting(true);
    try {
      const res = await runImport(file, false);
      const created = res.data.summary?.created || 0;
      const updated = res.data.summary?.updated || 0;
      const parts = [];
      if (created > 0) parts.push(`${created} angelegt`);
      if (updated > 0) parts.push(`${updated} aktualisiert`);
      toast.success(parts.length ? `Import erfolgreich: ${parts.join(', ')}` : 'Import abgeschlossen');
      onSuccess?.();
      handleClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message || 'Import fehlgeschlagen');
    } finally {
      setCommitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) handleClose(); else onOpenChange(true); }}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>
            CSV (Semikolon/Komma) oder Excel (.xlsx) hochladen. Vor dem
            Speichern wird automatisch eine Vorschau geprueft.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Schritt 1 */}
          <div className="flex items-center justify-between p-3 rounded-lg border bg-muted/20">
            <div>
              <p className="text-sm font-medium">1. Vorlage herunterladen</p>
              <p className="text-xs text-muted-foreground">Empfohlen, damit alle Spalten korrekt sind.</p>
            </div>
            <Button variant="outline" size="sm" onClick={handleDownloadTemplate} data-testid="bulk-template-download">
              <Download className="mr-2 h-4 w-4" />Vorlage
            </Button>
          </div>

          {/* Schritt 2 */}
          <div>
            <p className="text-sm font-medium mb-2">2. CSV- oder Excel-Datei auswaehlen</p>
            <FileDropzone
              accept=".csv,.txt,.xlsx"
              onUpload={handleUpload}
              uploading={uploading}
              filename={file?.name}
              hint="CSV (Semikolon/Komma) oder Excel (.xlsx) — max. 10 MB"
              testId="bulk-file-dropzone"
            />
          </div>

          {/* Schritt 3 - Vorschau */}
          {preview && (
            <div className="space-y-2">
              <div className="flex items-center justify-between flex-wrap gap-2">
                <p className="text-sm font-medium">3. Vorschau</p>
                <div className="flex gap-2 text-xs">
                  <Badge variant="outline" className="border-emerald-500/40 text-emerald-700">
                    {preview.summary.valid} OK
                  </Badge>
                  {(preview.summary.updated || 0) > 0 && (
                    <Badge variant="outline" className="border-blue-500/40 text-blue-700">
                      {preview.summary.updated} Update
                    </Badge>
                  )}
                  <Badge variant="outline" className="border-amber-500/40 text-amber-700">
                    {preview.summary.skipped} Skip
                  </Badge>
                  <Badge variant="outline" className="border-red-500/40 text-red-700">
                    {preview.summary.errors} Fehler
                  </Badge>
                  <span className="text-muted-foreground">von {preview.summary.total}</span>
                </div>
              </div>
              <div className="border rounded-lg max-h-[300px] overflow-y-auto">
                <table className="w-full text-xs">
                  <thead className="bg-muted/40 sticky top-0">
                    <tr>
                      <th className="text-left p-2 w-[60px]">Zeile</th>
                      <th className="text-left p-2 w-[40px]" />
                      <th className="text-left p-2">Name</th>
                      <th className="text-left p-2">Hinweis</th>
                    </tr>
                  </thead>
                  <tbody>
                    {preview.rows.map((r) => {
                      const info = STATUS_INFO[r.status] || STATUS_INFO.error;
                      return (
                        <tr key={r.row} className="border-t">
                          <td className="p-2 font-mono">{r.row}</td>
                          <td className="p-2">
                            <info.Icon className={`h-4 w-4 ${info.cls}`} />
                          </td>
                          <td className="p-2 font-medium">
                            {r.name || <span className="text-muted-foreground">—</span>}
                            {r.branch && <span className="text-muted-foreground"> · {r.branch}</span>}
                          </td>
                          <td className="p-2 text-muted-foreground">
                            {r.messages && r.messages.length > 0 ? r.messages.join(' · ') : '—'}
                            {r.changes && r.changes.length > 0 && (
                              <div className="mt-1 flex flex-wrap gap-1">
                                {r.changes.map((c) => (
                                  <span
                                    key={`${r.row}-${c.field}`}
                                    className="inline-flex items-center gap-1 rounded bg-blue-50 dark:bg-blue-950/40 px-1.5 py-0.5 text-[10px] text-blue-700 dark:text-blue-300 border border-blue-200/60 dark:border-blue-800/60"
                                  >
                                    <span className="font-medium">{c.label}:</span>
                                    <span className="line-through opacity-70">{c.old}</span>
                                    <span>→</span>
                                    <span className="font-semibold">{c.new}</span>
                                  </span>
                                ))}
                              </div>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>

        <DialogFooter className="gap-2">
          <Button variant="ghost" onClick={handleClose}>Abbrechen</Button>
          <Button
            onClick={handleCommit}
            disabled={!preview || ((preview.summary.valid || 0) + (preview.summary.updated || 0)) === 0 || committing}
            data-testid="bulk-commit-btn"
          >
            {committing
              ? 'Wird importiert...'
              : `${(preview?.summary.valid || 0) + (preview?.summary.updated || 0)} Eintrag/Einträge importieren`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
